import {
    Box,
    Checkbox,
    Divider,
    Grid,
    IconButton,
    Input,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Popover,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    ExpandMore,
    KeyboardArrowDown,
    KeyboardArrowRight,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import React, {
    ChangeEvent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { usePopoverOnButtonClick } from './filterHooks';
import { StyledFilterButton, StyledFilterText } from './SharedFilterComponents';
import UseDebounceInput from '../../../shared/DebounceInput';
export type ExperienceListItem = { id: number; name: string };

export type GroupedExperienceGroup = {
    typeValue: string;
    typeLabel: string;
    items: ExperienceListItem[];
};

const TYPE_LABEL: Record<string, string> = {
    activity: 'Activity',
    boat: 'Boat',
    commercial: 'Commercial',
    property: 'Property',
    vehicle: 'Vehicle',
};

function groupLabel(typeValue: string) {
    return (
        TYPE_LABEL[typeValue.toLowerCase()] ||
        typeValue.replace(/^\w/, (c) => c.toUpperCase())
    );
}

export function buildGroupedExperiences(
    modules: { id: number; name: string; type: string }[],
): GroupedExperienceGroup[] {
    if (!Array.isArray(modules) || modules.length === 0) return [];

    const byType = new Map<string, ExperienceListItem[]>();
    for (const m of modules) {
        const t = String(m.type).toLowerCase();
        if (!byType.has(t)) byType.set(t, []);
        byType.get(t)!.push({ id: m.id, name: m.name || '' });
    }
    byType.forEach((items) => {
        items.sort((a: ExperienceListItem, b: ExperienceListItem) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        );
    });
    const typeKeys = Array.from(byType.keys()).sort((a, b) =>
        groupLabel(a).localeCompare(groupLabel(b), undefined, {
            sensitivity: 'base',
        }),
    );
    return typeKeys.map((typeValue) => ({
        typeValue,
        typeLabel: groupLabel(typeValue),
        items: byType.get(typeValue)!,
    }));
}

export default function GroupedExperienceTypeFilter({
    id = 'type-experiences',
    groups,
    selectedModuleIds,
    onChange,
    label = 'Experience:',
    backgroundColor,
    border,
    minHeight,
    maxHeight,
    disabled,
}: {
    id?: string;
    groups: GroupedExperienceGroup[];
    selectedModuleIds: number[];
    onChange: (ids: number[]) => void;
    label?: string;
    backgroundColor?: string;
    border?: string;
    minHeight?: string;
    maxHeight?: string;
    disabled?: boolean;
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const { handleClick, handleClose, open, popoverId, anchorEl } =
        usePopoverOnButtonClick();
    const [searchText, setSearchText] = UseDebounceInput(10);
    const [localSelected, setLocalSelected] = useState<number[]>([]);
    /** Only `true` means expanded; omitted/`false` = collapsed. */
    const [expandedByType, setExpandedByType] = useState<
        Record<string, boolean>
    >({});

    const totalCount = useMemo(
        () => groups.reduce((n, g) => n + g.items.length, 0),
        [groups],
    );

    const allIdsInDataset = useMemo(
        () => groups.flatMap((g) => g.items.map((i) => i.id)),
        [groups],
    );

    useEffect(() => {
        setExpandedByType((prev) => {
            const next = { ...prev };
            for (const g of groups) {
                if (next[g.typeValue] === undefined) {
                    next[g.typeValue] = false;
                }
            }
            return next;
        });
    }, [groups]);

    useEffect(() => {
        setLocalSelected(selectedModuleIds);
    }, [selectedModuleIds]);

    const filteredGroups = useMemo(() => {
        if (!searchText?.trim()) return groups;
        const q = searchText.toLowerCase().replace(/\s/g, '');
        return groups
            .map((g) => ({
                ...g,
                items: g.items.filter((i) =>
                    i.name.toLowerCase().replace(/\s/g, '').includes(q),
                ),
            }))
            .filter((g) => g.items.length > 0);
    }, [groups, searchText]);

    const commitSelection = useCallback(
        (next: number[]) => {
            setLocalSelected(next);
            onChange(next);
        },
        [onChange],
    );

    const handleToggle = (moduleId: number) => () => {
        const currentIndex = localSelected.indexOf(moduleId);
        const next =
            currentIndex === -1
                ? [...localSelected, moduleId]
                : localSelected.filter((id) => id !== moduleId);
        commitSelection(next);
    };

    const onChangeSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            commitSelection([...allIdsInDataset]);
        } else {
            commitSelection([]);
        }
    };

    const allGloballySelected =
        totalCount > 0 &&
        localSelected.length === totalCount &&
        allIdsInDataset.every((id) => localSelected.includes(id));
    const someGloballySelected =
        localSelected.length > 0 && !allGloballySelected;

    const toggleCategory = (typeValue: string) => {
        const fullGroup = groups.find((g) => g.typeValue === typeValue);
        if (!fullGroup) return;
        const idsInCategory = fullGroup.items.map((i) => i.id);
        const allInCategorySelected = idsInCategory.every((id) =>
            localSelected.includes(id),
        );
        if (allInCategorySelected) {
            const drop = new Set(idsInCategory);
            commitSelection(localSelected.filter((id) => !drop.has(id)));
        } else {
            commitSelection(
                Array.from(new Set([...localSelected, ...idsInCategory])),
            );
        }
    };

    const toggleExpanded = (typeValue: string) => {
        setExpandedByType((prev) => {
            const wasExpanded = prev[typeValue] === true;
            return {
                ...prev,
                [typeValue]: !wasExpanded,
            };
        });
    };

    const categorySelectionState = (typeValue: string) => {
        const fullGroup = groups.find((g) => g.typeValue === typeValue);
        if (!fullGroup || fullGroup.items.length === 0) {
            return { checked: false, indeterminate: false };
        }
        const ids = fullGroup.items.map((i) => i.id);
        const count = ids.filter((id) => localSelected.includes(id)).length;
        return {
            checked: count === ids.length,
            indeterminate: count > 0 && count < ids.length,
        };
    };

    const summary =
        totalCount === 0
            ? ''
            : localSelected.length === 0
              ? 'Select'
              : localSelected.length === totalCount
                ? `All ${totalCount} selected`
                : `${localSelected.length} of ${totalCount} selected`;

    const showSearch = totalCount > 10;

    return (
        <>
            <StyledFilterButton
                disableElevation
                disabled={disabled || totalCount === 0}
                aria-describedby={popoverId}
                onClick={handleClick}
                variant={
                    disabled || totalCount === 0 ? 'contained' : 'outlined'
                }
                device={isMobile.toString()}
                sx={{
                    backgroundColor:
                        disabled || totalCount === 0
                            ? theme.palette.lightGrey.main
                            : open
                              ? theme.palette.neutral.main
                              : backgroundColor,
                    borderColor:
                        disabled || totalCount === 0
                            ? theme.palette.lightGrey.main
                            : open
                              ? theme.palette.info.main
                              : border,
                    color: open
                        ? theme.palette.textAccent.main
                        : theme.palette.midGrey.main,
                    minHeight: minHeight,
                    maxHeight: maxHeight,
                }}
            >
                <StyledFilterText variant={'body2'}>
                    <Grid sx={{ display: 'flex', flexDirection: 'row' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                marginRight: '5px',
                            }}
                        >
                            {label}
                        </Typography>
                        <Typography sx={{ fontSize: '14px' }}>
                            {summary}
                        </Typography>
                    </Grid>
                </StyledFilterText>
                <Box width={10} flexGrow={1} />
                <KeyboardArrowDown />
            </StyledFilterButton>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                disableAutoFocus={true}
                disableEnforceFocus={true}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <List
                    sx={{
                        maxHeight: '360px',
                        overflow: 'auto',
                        minWidth: 300,
                        py: 0,
                        px: 0,
                    }}
                >
                    {showSearch ? (
                        <ListItem sx={{ px: 2, pt: 1, pb: 0.5 }}>
                            <Input
                                defaultValue={searchText}
                                placeholder={'Search'}
                                fullWidth
                                endAdornment={
                                    <InputAdornment position={'end'}>
                                        <SearchIcon />
                                    </InputAdornment>
                                }
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        </ListItem>
                    ) : null}

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            px: 2,
                            py: 1.25,
                            boxSizing: 'border-box',
                            width: '100%',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                width: 42,
                                minHeight: 42,
                                pt: 0.25,
                            }}
                        >
                            <Checkbox
                                sx={{
                                    p: 0.5,
                                    color: 'text.primary',
                                }}
                                size='small'
                                tabIndex={-1}
                                disableRipple
                                checked={allGloballySelected}
                                onChange={onChangeSelectAll}
                                indeterminate={someGloballySelected}
                            />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
                            <Typography variant='body2' fontWeight={600}>
                                {allGloballySelected
                                    ? 'Unselect all'
                                    : 'Select all'}
                            </Typography>
                            <Typography
                                variant='caption'
                                color='text.secondary'
                                display='block'
                                sx={{ mt: 0.25 }}
                            >
                                {localSelected.length === 0
                                    ? 'No experiences selected'
                                    : `${localSelected.length} of ${totalCount} selected`}
                            </Typography>
                        </Box>
                    </Box>
                    <Divider />

                    {filteredGroups.map((group) => {
                        const expanded =
                            expandedByType[group.typeValue] === true;
                        const { checked, indeterminate } =
                            categorySelectionState(group.typeValue);
                        return (
                            <React.Fragment key={group.typeValue}>
                                <ListItem
                                    disablePadding
                                    sx={{
                                        borderTop: `1px solid ${theme.palette.divider}`,
                                        px: 1,
                                        py: 0,
                                        '&:hover': {
                                            backgroundColor:
                                                theme.palette.neutral.main,
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '100%',
                                            gap: 1,
                                            py: 0.75,
                                            pr: 0.5,
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                        <IconButton
                                            size='small'
                                            aria-label={
                                                expanded
                                                    ? 'Collapse category'
                                                    : 'Expand category'
                                            }
                                            onClick={() =>
                                                toggleExpanded(group.typeValue)
                                            }
                                            sx={{
                                                flexShrink: 0,
                                                width: 36,
                                                height: 36,
                                                p: 0.5,
                                            }}
                                        >
                                            {expanded ? (
                                                <ExpandMore fontSize='small' />
                                            ) : (
                                                <KeyboardArrowRight fontSize='small' />
                                            )}
                                        </IconButton>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                width: 40,
                                                height: 40,
                                            }}
                                        >
                                            <Checkbox
                                                sx={{
                                                    p: 0.5,
                                                    color: 'text.primary',
                                                }}
                                                size='small'
                                                tabIndex={-1}
                                                disableRipple
                                                checked={checked}
                                                indeterminate={indeterminate}
                                                onChange={() =>
                                                    toggleCategory(
                                                        group.typeValue,
                                                    )
                                                }
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                cursor: 'pointer',
                                                py: 0.25,
                                                alignSelf: 'center',
                                            }}
                                            onClick={() =>
                                                toggleCategory(group.typeValue)
                                            }
                                        >
                                            <Typography
                                                variant='body2'
                                                fontWeight={600}
                                            >
                                                {group.typeLabel}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </ListItem>
                                {expanded &&
                                    group.items.map((item) => (
                                        <ListItem
                                            key={item.id}
                                            disablePadding
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor:
                                                        theme.palette.neutral
                                                            .main,
                                                },
                                            }}
                                        >
                                            <ListItemButton
                                                onClick={handleToggle(item.id)}
                                                dense
                                                sx={{
                                                    pl: 4,
                                                    '&:hover': {
                                                        backgroundColor:
                                                            'transparent',
                                                    },
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={{ minWidth: 40 }}
                                                >
                                                    <Checkbox
                                                        sx={{
                                                            p: 0,
                                                            color: 'text.primary',
                                                        }}
                                                        size={'small'}
                                                        edge='start'
                                                        tabIndex={-1}
                                                        disableRipple
                                                        checked={
                                                            localSelected.indexOf(
                                                                item.id,
                                                            ) !== -1
                                                        }
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={item.name}
                                                    primaryTypographyProps={{
                                                        variant: 'body2',
                                                    }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                            </React.Fragment>
                        );
                    })}
                </List>
            </Popover>
        </>
    );
}
